import * as constants from './action-constants';
import { DESIGN_FILTER_OPTIONS } from 'utils/constants/designViewer';
import { Product } from 'redux/models';
import { getNextMobileHotspotsView } from './utils';
import { MOBILE_HOTSPOTS_VIEWS } from './constants';

const initialState = {
  activeRenderId: null, // Design Viewer spotlights one render at a time
  activePDPTooltipProductId: null, // One PDP tooltip open at a time with one product
  activePDPTooltipHotSpotIndex: null, // Needed to identify hotspot and re-render tooltip
  designsFilter: DESIGN_FILTER_OPTIONS[0], // Default options to show all designs
  hotspotActive: false, // bool value if hotspot tooltip open
  renderKey: 0, // used as key to animate renders
  slideDirection: null, // used to direct renders
  studioViewMode: 'VIEW', // three possible modes ['VIEW', 'SHOP', 'SWAP']
  showFullScreenRender: false, // toggle on or off Full Screen view of design render
  panoramaViewerOpen: false, // whether or not the 360 panorama viewer is currently open
  panoramaViewerRenderId: -1, // the renderId of the 360 render that should be open
  popupAlternatives: [], // alternative products to the currently selected one
  awaitingAlternatives: false, // true if we're waiting for a response from the alternatives request
  alternativesError: null, // the error, if any, we received from an alternatives request
  alternativesId: -1, // a UID for alternatives requests so we don't get alternatives for the wrong product
  allowSwipe: true, // whether the renders container accepts swipe events or not. Used to prevent swiping when panning a render while zoomed-in
  forceRenderOnDesignChange: '', // set this to first|last to force render when design changes
  moreMenuOpen: false, // toggle on or off to display messenger studio more menu
  designNotification: '', // notification text to display as pop up on messenger studio
  showMobileHotspots: false, // whether or not to display hotspots on mobile
  hasSeenMobileHotspots: false, // whether mobile hotspots have been shown (set to true on first showMobileHotspots change)
  mobileHotspotsView: MOBILE_HOTSPOTS_VIEWS.ALL_HOTSPOTS // value determining hotspot view type (show all hotspots, swapping hotspots only, etc)
};

export const designViewerReducer = (state = initialState, action) => {
  switch (action.type) {
    case constants.SELECT_DESIGNS_FILTER:
      return {
        ...state,
        designsFilter: action.payload
      };

    case constants.SELECT_PDP_TOOLTIP_PRODUCT:
      return {
        ...state,
        activePDPTooltipProductId: action.payload.productId,
        activePDPTooltipHotSpotIndex: action.payload.hotSpotIdx
      };

    case constants.RECEIVE_ALTERNATIVES:
    {
      // create Product models for alternatives
      let alternatives = [];
      let count = 0;
      let altProducts = action.payload.results;

      for (let index in altProducts) {
        if (count >= 3) { break; }
        alternatives.push(Product.createProductFromWarehouseProductResponse({whProduct: altProducts[index]}));
        count++;
      }

      return {
        ...state,
        popupAlternatives: alternatives
      };
    }
    case constants.AWAITING_ALTERNATIVES:
      return {
        ...state,
        awaitingAlternatives: action.payload
      };
    case constants.ALTERNATIVES_REQUEST_ERROR:
      return {
        ...state,
        alternativesError: action.payload,
        popupAlternatives: []
      };
    case constants.ALTERNATIVES_ID:
      return {
        ...state,
        alternativesId: action.payload
      };
    case constants.SELECT_RENDER: {
      const {
        renderId,
        disableSlideAnimation,
        slideDirection
      } = action.payload;

      // disable slide animations for full screen renders by default
      const shouldUpdateRenderKey = !(disableSlideAnimation || state.showFullScreenRender);

      return {
        ...state,
        activeRenderId: renderId,
        forceRenderOnDesignChange: '',
        renderKey: shouldUpdateRenderKey ? state.renderKey + 1 : state.renderKey,
        slideDirection: shouldUpdateRenderKey ? slideDirection : null,
        hotspotActive: false
      };
    }
    case constants.CLEAR_RENDER:
      return {
        ...state,
        activeRenderId: null,
        renderKey: 0,
        slideDirection: null,
        hotspotActive: false
      };

    case constants.SET_FORCE_RENDER_ON_DESIGN_CHANGE:
      return {
        ...state,
        forceRenderOnDesignChange: action.payload
      };

    case constants.SELECT_VIEW_MODE:
      return {
        ...state,
        studioViewMode: action.payload
      };

    case constants.UPDATE_HOTSPOT_STATUS:
      return {
        ...state,
        hotspotActive: action.payload
      };

    case constants.TOGGLE_FULL_SCREEN_RENDER:
      return {
        ...state,
        showFullScreenRender: !state.showFullScreenRender,
        studioViewMode: !state.showFullScreenRender ? 'SHOP' : 'VIEW'
      };

    case constants.SET_PANORAMA_VIEWER_OPEN_FLAG:
      const open = action.payload.open;
      const renderId = action.payload.renderId;
      return {
        ...state,
        panoramaViewerOpen: open,
        panoramaViewerRenderId: renderId
      };

    case constants.SET_ALLOW_SWIPE:
      return {
        ...state,
        allowSwipe: action.payload
      };

    case constants.SET_MORE_MENU_OPEN:
      return {
        ...state,
        moreMenuOpen: action.payload
      };

    case constants.SET_DESIGN_NOTIFICATION:
      return {
        ...state,
        designNotification: action.payload
      };

    case constants.SET_SHOW_MOBILE_HOTSPOTS: {
      const showMobileHotspots = action.payload;

      // Update hasSeenMobileHotspots based on previous hasSeenMobileHotspots value
      // or showMobileHotspots value
      const hasSeenMobileHotspots = state.hasSeenMobileHotspots || showMobileHotspots;
      const mobileHotspotsView = showMobileHotspots
        ? getNextMobileHotspotsView(state.mobileHotspotsView) : state.mobileHotspotsView;

      return {
        ...state,
        showMobileHotspots,
        mobileHotspotsView,
        hasSeenMobileHotspots
      };
    }

    case constants.SET_MOBILE_HOTSPOTS_VIEW: {
      const mobileHotspotsView = action.payload;
      return {
        ...state,
        showMobileHotspots: true,
        mobileHotspotsView
      };
    }

    default:
      return state;
  }
};
